import { Breadcrumb, HeaderToolbar, Spinner } from "@xola/ui-kit";
import { useCounter, useMemoizedFn } from "ahooks";
import React from "react";
import { useParams } from "react-router-dom";
import { BrokenAppPlaceholder } from "../embeddedApps/BrokenAppPlaceholder";
import { IFRAME_SANDBOX } from "../embeddedApps/constants";
import { EmbeddedAppChrome } from "../embeddedApps/EmbeddedAppChrome";
import { useEmbeddedAppStore, useIsAppBroken } from "../embeddedApps/embeddedAppStore";
import { useEmbeddedAppSession } from "../embeddedApps/useEmbeddedAppSession";
import { useArePluginsLoadedFromStore, useInstalledPluginsFromStore } from "../stores/globalStore";
import { Page } from "./Page";
import { NoTranslate } from "@/components/Common/NoTranslate";
import { ColumnLayout } from "@/components/Layout/ColumnLayout";

interface EmbeddedAppPageProps {
    // Fixed routes resolve the plugin themselves instead of reading it from the URL.
    appId?: string;
}

export const EmbeddedAppPage = ({ appId: fixedAppId }: EmbeddedAppPageProps) => {
    const { id: routeAppId } = useParams<{ id: string }>();
    const appId = fixedAppId ?? routeAppId;
    const plugins = useInstalledPluginsFromStore();
    const arePluginsLoaded = useArePluginsLoadedFromStore();
    const actions = useEmbeddedAppStore.getState().actions;
    const isBroken = useIsAppBroken(appId);
    const plugin = plugins.find((p) => p.id === appId);

    // Only meaningful once the plugin list has landed; before that a missing plugin just means "still loading".
    const isUnavailable = arePluginsLoaded && !(plugin?.ui?.isEmbeddedApp && plugin.ui.embeddedUrl);
    // The store keeps stale plugin data visible during a refetch (keepPreviousData), so this is only true
    // on a cold load before the plugin list has landed even once - the only window where `plugin` can be
    // undefined without meaning "unavailable".
    const isPluginListLoading = !arePluginsLoaded && !plugin;

    const appName = plugin?.name ?? "";
    const embeddedUrl = plugin?.ui?.embeddedUrl ?? "";
    const launchSection = plugin?.ui?.triggers?.[0]?.location?.split(".")?.[0];
    const sectionLabel = launchSection ? launchSection.charAt(0).toUpperCase() + launchSection.slice(1) : undefined;

    const [retryKey, { inc: incRetryKey }] = useCounter(0);

    const handleRetry = useMemoizedFn(() => {
        actions.resetBroken(appId);
        incRetryKey();
    });

    // handleLoad is a stable useMemoizedFn, so these consumers need no memoization.
    const {
        iframeRef,
        handleLoad,
        status: sessionStatus,
        isLongWaking,
    } = useEmbeddedAppSession({
        plugin,
        onHandshakeFailed: () => actions.markBroken(appId),
        onAppBroken: () => actions.markBroken(appId),
        onReload: () => actions.resetBroken(appId),
    });

    // The session hook leaves "broken" out of its status on purpose, so the chrome label and the
    // canvas body have to be combined here or the header keeps showing a stale loading state.
    const status = isBroken ? "broken" : sessionStatus;

    if (isUnavailable) {
        return (
            <Page title="App unavailable">
                <ColumnLayout>
                    <HeaderToolbar>
                        <HeaderToolbar.Breadcrumb className="flex-grow truncate border-r-0">
                            <Breadcrumb.Item>{sectionLabel ?? "Back"}</Breadcrumb.Item>
                            <Breadcrumb.Item>App unavailable</Breadcrumb.Item>
                        </HeaderToolbar.Breadcrumb>
                    </HeaderToolbar>
                    <ColumnLayout.Fill>
                        {(height) => (
                            <BrokenAppPlaceholder
                                appId={appId}
                                style={{ height }}
                                message="This app is not available."
                            />
                        )}
                    </ColumnLayout.Fill>
                </ColumnLayout>
            </Page>
        );
    }

    if (isPluginListLoading) {
        return (
            <Page title="">
                <ColumnLayout>
                    <HeaderToolbar>
                        <HeaderToolbar.Breadcrumb className="flex-grow truncate border-r-0">
                            <Breadcrumb.Item>Back</Breadcrumb.Item>
                        </HeaderToolbar.Breadcrumb>
                    </HeaderToolbar>
                    <ColumnLayout.Fill>
                        {(height) => (
                            <div className="flex items-center justify-center" style={{ height }}>
                                <Spinner />
                            </div>
                        )}
                    </ColumnLayout.Fill>
                </ColumnLayout>
            </Page>
        );
    }

    return (
        <Page title={appName}>
            <ColumnLayout>
                <HeaderToolbar>
                    <HeaderToolbar.Breadcrumb className="flex-grow truncate border-r-0">
                        <Breadcrumb.Item>{sectionLabel ?? "Back"}</Breadcrumb.Item>
                        <NoTranslate>
                            <Breadcrumb.Item>{appName}</Breadcrumb.Item>
                        </NoTranslate>
                    </HeaderToolbar.Breadcrumb>
                </HeaderToolbar>
                <EmbeddedAppChrome
                    isLongWaking={isLongWaking}
                    appName={appName}
                    status={status}
                    onReload={handleRetry}
                />
                <ColumnLayout.Fill>
                    {(height) =>
                        isBroken ? (
                            <BrokenAppPlaceholder appId={appId} style={{ height }} onRetry={handleRetry} />
                        ) : (
                            <iframe
                                key={retryKey}
                                ref={iframeRef}
                                title={appName}
                                src={embeddedUrl}
                                style={{ height }}
                                className="w-full border-0"
                                sandbox={IFRAME_SANDBOX}
                                onLoad={handleLoad}
                            />
                        )
                    }
                </ColumnLayout.Fill>
            </ColumnLayout>
        </Page>
    );
};
